import { create } from 'zustand';
import type {
  ClarifyDashboardData,
  ClarifyListState,
  ClarifyRequestDetail,
  ClarifyWorkspaceMember,
  CreateClarifyRequestInput,
  RejectClarifyRequestInput,
  ReviewClarifyRequestInput,
  TriageClarifyRequestInput,
} from '@/types/clarify';
import { EMPTY_CLARIFY_LIST } from '@/types/clarify';
import {
  clarifyApi,
  getOptionalAssignableMembers,
  getOptionalClarifyList,
} from './clarifyApi';

const EMPTY_DASHBOARD: ClarifyDashboardData = {
  requestCount: 0,
  inProgressCount: 0,
  inReviewCount: 0,
  solvedCount: 0,
  urgent: [],
  dueSoon: [],
  overdue: [],
  recentActivity: [],
};

interface ClarifyState {
  dashboard: ClarifyDashboardData;
  myRequests: ClarifyListState;
  myTasks: ClarifyListState;
  triageQueue: ClarifyListState;
  reviewQueue: ClarifyListState;
  assignableMembers: ClarifyWorkspaceMember[];
  selectedRequest: ClarifyRequestDetail | null;
  loading: boolean;
  selectedRequestLoading: boolean;
  error: string | null;
  loadAll: (workspaceId: string, token?: string | null) => Promise<void>;
  loadDetail: (workspaceId: string, requestId: string, token?: string | null) => Promise<void>;
  createRequest: (workspaceId: string, input: CreateClarifyRequestInput, token?: string | null) => Promise<void>;
  triageRequest: (workspaceId: string, requestId: string, input: TriageClarifyRequestInput, token?: string | null) => Promise<void>;
  rejectRequest: (workspaceId: string, requestId: string, input: RejectClarifyRequestInput, token?: string | null) => Promise<void>;
  linkInterviewSession: (workspaceId: string, requestId: string, sessionId: string, token?: string | null) => Promise<void>;
  submitForReview: (workspaceId: string, requestId: string, note?: string, token?: string | null) => Promise<void>;
  reviewRequest: (workspaceId: string, requestId: string, input: ReviewClarifyRequestInput, token?: string | null) => Promise<void>;
  clearSelection: () => void;
}

export const useClarifyStore = create<ClarifyState>()((set, get) => ({
  dashboard: EMPTY_DASHBOARD,
  myRequests: EMPTY_CLARIFY_LIST,
  myTasks: EMPTY_CLARIFY_LIST,
  triageQueue: EMPTY_CLARIFY_LIST,
  reviewQueue: EMPTY_CLARIFY_LIST,
  assignableMembers: [],
  selectedRequest: null,
  loading: false,
  selectedRequestLoading: false,
  error: null,

  loadAll: async (workspaceId, token) => {
    set({ loading: true, error: null });
    try {
      const [dashboard, myRequests, myTasks, triageQueue, reviewQueue, assignableMembers] = await Promise.all([
        clarifyApi.getDashboard(workspaceId, token),
        clarifyApi.getList(workspaceId, 'my-requests', token),
        clarifyApi.getList(workspaceId, 'my-tasks', token),
        getOptionalClarifyList(workspaceId, 'triage', token),
        getOptionalClarifyList(workspaceId, 'review', token),
        getOptionalAssignableMembers(workspaceId, token),
      ]);

      set({
        dashboard,
        myRequests,
        myTasks,
        triageQueue,
        reviewQueue,
        assignableMembers,
        loading: false,
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Clarify-Daten konnten nicht geladen werden.',
      });
    }
  },

  loadDetail: async (workspaceId, requestId, token) => {
    set({ selectedRequestLoading: true, error: null });
    try {
      const selectedRequest = await clarifyApi.getDetail(workspaceId, requestId, token);
      set({ selectedRequest, selectedRequestLoading: false });
    } catch (error) {
      set({
        selectedRequestLoading: false,
        error: error instanceof Error ? error.message : 'Request-Details konnten nicht geladen werden.',
      });
    }
  },

  createRequest: async (workspaceId, input, token) => {
    await clarifyApi.createRequest(workspaceId, input, token);
    await get().loadAll(workspaceId, token);
  },

  triageRequest: async (workspaceId, requestId, input, token) => {
    await clarifyApi.triageRequest(workspaceId, requestId, input, token);
    await get().loadAll(workspaceId, token);
    if (get().selectedRequest?.id === requestId) {
      await get().loadDetail(workspaceId, requestId, token);
    }
  },

  rejectRequest: async (workspaceId, requestId, input, token) => {
    await clarifyApi.rejectRequest(workspaceId, requestId, input, token);
    await get().loadAll(workspaceId, token);
    if (get().selectedRequest?.id === requestId) {
      await get().loadDetail(workspaceId, requestId, token);
    }
  },

  linkInterviewSession: async (workspaceId, requestId, sessionId, token) => {
    await clarifyApi.linkInterviewSession(workspaceId, requestId, sessionId, token);
    await get().loadAll(workspaceId, token);
    if (get().selectedRequest?.id === requestId) {
      await get().loadDetail(workspaceId, requestId, token);
    }
  },

  submitForReview: async (workspaceId, requestId, note, token) => {
    await clarifyApi.submitForReview(workspaceId, requestId, note, token);
    await get().loadAll(workspaceId, token);
    if (get().selectedRequest?.id === requestId) {
      await get().loadDetail(workspaceId, requestId, token);
    }
  },

  reviewRequest: async (workspaceId, requestId, input, token) => {
    await clarifyApi.reviewRequest(workspaceId, requestId, input, token);
    await get().loadAll(workspaceId, token);
    if (get().selectedRequest?.id === requestId) {
      await get().loadDetail(workspaceId, requestId, token);
    }
  },

  clearSelection: () => set({ selectedRequest: null, selectedRequestLoading: false }),
}));